import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { API } from '../App';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from './ui/tooltip';
import { toast } from 'sonner';
import { useBranch } from '../context/BranchContext';
import {
  Package, Truck, ChevronDown, ChevronUp, ChevronsUpDown, RefreshCw, X,
  CheckCircle2, Clock, ArrowRight, Printer, Send, Search,
} from 'lucide-react';

const enconOrange = '#ee7203';

const fmt = (n) => {
  if (n == null || n === '' || isNaN(n)) return '—';
  return Number(n).toLocaleString('en-GB', { maximumFractionDigits: 2 });
};
const fmtDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return null; }
};

const FINDING_STYLE = {
  'COVERED BY LOCAL FREE STOCK': { label: 'Covered locally', color: '#15803d', bg: '#f0fdf4' },
  'FULLY COVERED FROM GROUP STOCK': { label: 'Fully covered', color: '#15803d', bg: '#f0fdf4' },
  'PARTIAL GROUP STOCK COVERAGE': { label: 'Partial cover', color: '#b45309', bg: '#fffbeb' },
  'NO OLD GROUP FREE STOCK': { label: 'No stock found', color: '#b91c1c', bg: '#fef2f2' },
};

// ─── Search/filter/sort helpers (shared by receive + donor tables) ─────────
const matchesSearch = (item, query) => {
  if (!query) return true;
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (
    (item.product_code || '').toLowerCase().includes(q) ||
    (item.description || '').toLowerCase().includes(q) ||
    (item.supplier_name || '').toLowerCase().includes(q)
  );
};

const matchesBranch = (item, branchCode, section) => {
  if (!branchCode) return true;
  if (section === 'donor') return item.destination_branch_code === branchCode;
  if (section === 'group') return (item.donor_branches || []).some(d => d.branch_code === branchCode);
  return true; // 'own' section has no branch dimension
};

const getSortValue = (item, key, section) => {
  switch (key) {
    case 'product_code': return item.product_code || '';
    case 'description': return item.description || '';
    case 'qty': return section === 'donor' ? (item.qty_to_send ?? 0) : (item.net_new_order_qty ?? 0);
    case 'on_hand': return item.on_hand ?? 0;
    case 'incoming': return item.incoming ?? 0;
    case 'due_out': return item.due_out ?? 0;
    case 'branch':
      return section === 'donor'
        ? (item.destination_branch_code || '')
        : (item.donor_branches?.[0]?.branch_code || '');
    default: return '';
  }
};

const applyFilterSort = (groups, { searchQuery, branchFilter, sortConfig, section }) => {
  return groups
    .map(g => {
      let items = g.items.filter(it => matchesSearch(it, searchQuery) && matchesBranch(it, branchFilter, section));
      if (sortConfig.key) {
        items = [...items].sort((a, b) => {
          const av = getSortValue(a, sortConfig.key, section);
          const bv = getSortValue(b, sortConfig.key, section);
          const cmp = typeof av === 'number' && typeof bv === 'number'
            ? av - bv
            : String(av).localeCompare(String(bv));
          return sortConfig.dir === 'asc' ? cmp : -cmp;
        });
      }
      return { ...g, items, item_count: items.length };
    })
    .filter(g => g.items.length > 0);
};

// ─── Sortable column header cell ────────────────────────────────────────────
function SortTh({ label, sortKey, align = 'left', sortConfig, onSort, className = '' }) {
  const active = sortConfig.key === sortKey;
  return (
    <th
      className={`px-3 py-2 font-semibold cursor-pointer select-none hover:text-gray-700 ${align === 'right' ? 'text-right' : 'text-left'} ${className}`}
      onClick={() => onSort(sortKey)}
      data-testid={`efficiency-sort-${sortKey}`}
    >
      <span className={`inline-flex items-center gap-1 ${align === 'right' ? 'flex-row-reverse' : ''}`}>
        {label}
        {active
          ? (sortConfig.dir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)
          : <ChevronsUpDown className="w-3 h-3 opacity-30" />}
      </span>
    </th>
  );
}

// ─── Branch pill with hover tooltip showing the full branch name ──────────
function BranchPill({ code, name, extra, colorClass = 'bg-blue-50 text-blue-700 border-blue-200', onClick }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          onClick={onClick}
          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold border whitespace-nowrap ${colorClass} ${onClick ? 'cursor-pointer hover:brightness-95' : 'cursor-default'}`}
        >
          {code} <ArrowRight className="w-2.5 h-2.5" /> {extra}
        </span>
      </TooltipTrigger>
      <TooltipContent>{onClick ? `Create transfer from ${name || code}` : (name || code)}</TooltipContent>
    </Tooltip>
  );
}

// ─── Inline notes box (rounded, auto-saves) ────────────────────────────────────
function InlineNotesBox({ item, onSave }) {
  const [value, setValue] = useState(item.notes || '');
  const timerRef = useRef(null);

  // Keep in sync if the item's saved notes change from elsewhere (e.g. reload)
  useEffect(() => { setValue(item.notes || ''); }, [item.notes]);

  const handleChange = (val) => {
    setValue(val);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => onSave(item, val), 800);
  };

  return (
    <input
      type="text"
      value={value}
      onChange={e => handleChange(e.target.value)}
      placeholder="Add note…"
      className="w-full text-xs border border-gray-300 rounded-lg px-2.5 py-1.5 bg-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-400 transition-colors"
      data-testid={`efficiency-notes-input-${item.product_code}`}
    />
  );
}

// ─── Existing-transfer note — clickable link shown when a matching Stock
// Transfer request already exists, instead of/above the manual notes box ────
function ExistingTransferNote({ branchName, transferRef, onClick }) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-1 text-[11px] font-semibold text-blue-700 hover:text-blue-900 hover:underline mb-1 text-left"
      data-testid="efficiency-existing-transfer-link"
    >
      <Truck className="w-3 h-3 flex-shrink-0" />
      Transfer on from {branchName}{transferRef ? ` (${transferRef})` : ''}
    </button>
  );
}

// ─── One item row ────────────────────────────────────────────────────
function EfficiencyItemRow({ item, section, onToggleActioned, onSaveNotes, onCreateTransfer, onViewTransfer }) {
  const finding = FINDING_STYLE[item.finding] || { label: item.finding, color: '#374151', bg: '#f9fafb' };
  const hasExistingTransfer = !!item.existing_transfer_id;
  const isActioned = !!item.is_actioned || hasExistingTransfer;
  const rowBg = isActioned ? 'bg-green-50/70 hover:bg-green-50' : 'hover:bg-gray-50';

  return (
    <tr className={`transition-colors align-top ${rowBg}`} data-testid={`efficiency-row-${section}-${item.product_code}`}>
      <td className="px-3 py-2.5">
        <span className="font-mono text-xs font-medium text-gray-700 whitespace-nowrap">{item.product_code}</span>
      </td>
      <td className="px-3 py-2.5">
        <span className="block text-sm text-gray-700 leading-snug" title={item.description}>{item.description || '—'}</span>
      </td>
      <td className="px-2 py-2.5 text-right whitespace-nowrap text-xs text-gray-600">{fmt(item.on_hand)}</td>
      <td className="px-2 py-2.5 text-right whitespace-nowrap text-xs text-purple-600">{fmt(item.incoming)}</td>
      <td className="px-2 py-2.5 text-right whitespace-nowrap text-xs text-red-500">{fmt(item.due_out)}</td>
      <td className="px-3 py-2.5 text-right whitespace-nowrap">
        <span className="font-mono text-sm text-gray-700">{fmt(item.net_new_order_qty)}</span>
        {item.stock_unit && <span className="text-gray-400 text-[11px] ml-1">{item.stock_unit}</span>}
      </td>
      {section === 'own' ? (
        <td className="px-3 py-2.5 text-right whitespace-nowrap">
          <span className="font-mono text-sm text-green-700">{fmt(item.old_free_stock_here)}</span>
        </td>
      ) : (
        <td className="px-3 py-2.5">
          {item.donor_branches?.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {item.donor_branches.map((d, i) => (
                <BranchPill
                  key={i}
                  code={d.branch_code}
                  name={d.branch_name}
                  extra={fmt(d.qty)}
                  onClick={() => onCreateTransfer(item, d)}
                />
              ))}
            </div>
          ) : (
            <span className="text-xs text-gray-400">—</span>
          )}
          {item.remaining_external_buy_qty > 0 && (
            <div className="text-[10px] text-red-600 mt-1">{fmt(item.remaining_external_buy_qty)} still to buy externally</div>
          )}
        </td>
      )}
      <td className="px-3 py-2.5">
        <span
          className="inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap"
          style={{ color: finding.color, backgroundColor: finding.bg }}
        >
          {finding.label}
        </span>
      </td>
      <td className="px-3 py-2.5">
        {hasExistingTransfer && (
          <ExistingTransferNote
            branchName={item.existing_transfer_branch_name}
            transferRef={item.existing_transfer_ref}
            onClick={() => onViewTransfer(item.existing_transfer_id)}
          />
        )}
        <InlineNotesBox item={item} onSave={onSaveNotes} />
      </td>
      <td className="px-2 py-2.5 text-center">
        <Checkbox
          checked={isActioned}
          disabled={hasExistingTransfer}
          onCheckedChange={() => onToggleActioned(item)}
          className={isActioned ? 'border-green-500' : ''}
          title={hasExistingTransfer ? 'Auto-ticked: a transfer for this is already on' : undefined}
        />
      </td>
    </tr>
  );
}

// ─── Supplier group (accordion) ──────────────────────────────────────────────────
function SupplierGroup({ group, section, expanded, onToggle, onToggleActioned, onSaveNotes, sortConfig, onSort, onCreateTransfer, onViewTransfer }) {
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
      >
        <span className="font-semibold text-sm text-gray-800">{group.supplier_name}</span>
        <div className="flex items-center gap-2">
          <span className="text-xs px-2 py-0.5 rounded-full font-semibold text-white" style={{ backgroundColor: enconOrange }}>
            {group.item_count}
          </span>
          {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </div>
      </button>
      {expanded && (
        <div className="overflow-x-auto border-t border-gray-200">
          <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '110px' }} />
              <col />
              <col style={{ width: '64px' }} />
              <col style={{ width: '64px' }} />
              <col style={{ width: '64px' }} />
              <col style={{ width: '96px' }} />
              <col style={{ width: section === 'own' ? '110px' : '220px' }} />
              <col style={{ width: '120px' }} />
              <col style={{ width: '220px' }} />
              <col style={{ width: '44px' }} />
            </colgroup>
            <thead>
              <tr className="bg-gray-50 text-[11px] text-gray-500 uppercase tracking-wide border-b border-gray-200">
                <SortTh label="Code" sortKey="product_code" sortConfig={sortConfig} onSort={onSort} />
                <SortTh label="Description" sortKey="description" sortConfig={sortConfig} onSort={onSort} />
                <SortTh label="On Hand" sortKey="on_hand" align="right" sortConfig={sortConfig} onSort={onSort} />
                <SortTh label="In" sortKey="incoming" align="right" sortConfig={sortConfig} onSort={onSort} />
                <SortTh label="Out" sortKey="due_out" align="right" sortConfig={sortConfig} onSort={onSort} />
                <SortTh label="New Order" sortKey="qty" align="right" sortConfig={sortConfig} onSort={onSort} />
                {section === 'own' ? (
                  <th className="px-3 py-2 text-left font-semibold">Local Stock</th>
                ) : (
                  <SortTh label="Donor Branch(es)" sortKey="branch" sortConfig={sortConfig} onSort={onSort} />
                )}
                <th className="px-3 py-2 text-left font-semibold">Finding</th>
                <th className="px-3 py-2 text-left font-semibold">Notes</th>
                <th className="px-2 py-2 text-center font-semibold"><CheckCircle2 className="w-3.5 h-3.5 mx-auto" /></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {group.items.map(item => (
                <EfficiencyItemRow
                  key={item.product_code}
                  item={item}
                  section={section}
                  onToggleActioned={onToggleActioned}
                  onSaveNotes={onSaveNotes}
                  onCreateTransfer={onCreateTransfer}
                  onViewTransfer={onViewTransfer}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Donor item row (read-only — this branch as the SOURCE for another branch) ─
function DonorItemRow({ item, onViewTransfer, onToggleActioned }) {
  const isPartial = !item.is_full_supply;
  const rowBg = item.is_actioned
    ? 'bg-green-50/70 hover:bg-green-50'
    : isPartial ? 'bg-amber-50/60 hover:bg-amber-50' : 'hover:bg-gray-50';
  return (
    <tr className={`transition-colors align-top ${rowBg}`} data-testid={`efficiency-donor-row-${item.product_code}-${item.destination_branch_code}`}>
      <td className="px-4 py-4">
        <span className="font-mono text-xs font-medium text-gray-700 whitespace-nowrap">{item.product_code}</span>
      </td>
      <td className="px-4 py-4">
        <span className="block text-sm text-gray-700 leading-relaxed" title={item.description}>{item.description || '—'}</span>
      </td>
      <td className="px-3 py-4 text-right whitespace-nowrap text-xs text-gray-600">{fmt(item.on_hand)}</td>
      <td className="px-3 py-4 text-right whitespace-nowrap text-xs text-purple-600">{fmt(item.incoming)}</td>
      <td className="px-3 py-4 text-right whitespace-nowrap text-xs text-red-500">{fmt(item.due_out)}</td>
      <td className="px-4 py-4 text-right whitespace-nowrap">
        <div className="flex items-baseline justify-end gap-1">
          <span className="font-mono text-sm text-blue-700">{fmt(item.qty_to_send)}</span>
          {item.stock_unit && <span className="text-gray-400 text-[11px]">{item.stock_unit}</span>}
        </div>
        <div className="mt-1.5 flex justify-end">
          {isPartial ? (
            <span className="inline-block px-2 py-0.5 rounded-full text-[9px] font-bold uppercase text-amber-700 bg-amber-100 border border-amber-300 whitespace-nowrap" title="This branch can only supply part of what's needed — another branch or an external buy covers the rest">
              Partial
            </span>
          ) : (
            <span className="inline-block px-2 py-0.5 rounded-full text-[9px] font-bold uppercase text-green-700 bg-green-100 border border-green-300 whitespace-nowrap">
              Full
            </span>
          )}
        </div>
      </td>
      <td className="px-4 py-4">
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold bg-blue-50 text-blue-700 border border-blue-200 whitespace-nowrap cursor-default">
              <ArrowRight className="w-2.5 h-2.5" /> {item.destination_branch_name} ({item.destination_branch_code})
            </span>
          </TooltipTrigger>
          <TooltipContent>{item.destination_branch_name}</TooltipContent>
        </Tooltip>
        <div className="text-[10px] text-gray-500 mt-2 whitespace-nowrap">
          <span className="text-purple-600">In: {fmt(item.destination_incoming)}</span>
          <span className="mx-1.5 text-gray-300">·</span>
          <span className="text-red-500">Out: {fmt(item.destination_due_out)}</span>
        </div>
        {item.existing_transfer_id && (
          <div className="mt-1.5">
            <ExistingTransferNote
              branchName={item.destination_branch_name}
              transferRef={item.existing_transfer_ref}
              onClick={() => onViewTransfer(item.existing_transfer_id)}
            />
          </div>
        )}
      </td>
      <td className="px-3 py-4 text-center">
        <Checkbox
          checked={!!item.is_actioned}
          onCheckedChange={() => onToggleActioned(item)}
          className={item.is_actioned ? 'border-green-500' : ''}
          data-testid={`efficiency-donor-actioned-${item.product_code}-${item.destination_branch_code}`}
        />
      </td>
    </tr>
  );
}

// ─── Donor supplier group (accordion, read-only) ────────────────────────────
function DonorSupplierGroup({ group, expanded, onToggle, sortConfig, onSort, onViewTransfer, onToggleActioned }) {
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
        data-testid={`efficiency-donor-group-toggle-${group.supplier_name}`}
      >
        <span className="font-semibold text-sm text-gray-800">{group.supplier_name}</span>
        <div className="flex items-center gap-2">
          <span className="text-xs px-2 py-0.5 rounded-full font-semibold text-white bg-blue-600">
            {group.item_count}
          </span>
          {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </div>
      </button>
      {expanded && (
        <div className="overflow-x-auto border-t border-gray-200">
          <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '110px' }} />
              <col />
              <col style={{ width: '70px' }} />
              <col style={{ width: '70px' }} />
              <col style={{ width: '70px' }} />
              <col style={{ width: '150px' }} />
              <col style={{ width: '230px' }} />
              <col style={{ width: '50px' }} />
            </colgroup>
            <thead>
              <tr className="bg-gray-50 text-[11px] text-gray-500 uppercase tracking-wide border-b border-gray-200">
                <SortTh label="Code" sortKey="product_code" sortConfig={sortConfig} onSort={onSort} />
                <SortTh label="Description" sortKey="description" sortConfig={sortConfig} onSort={onSort} />
                <SortTh label="On Hand" sortKey="on_hand" align="right" sortConfig={sortConfig} onSort={onSort} />
                <SortTh label="In" sortKey="incoming" align="right" sortConfig={sortConfig} onSort={onSort} />
                <SortTh label="Out" sortKey="due_out" align="right" sortConfig={sortConfig} onSort={onSort} />
                <SortTh label="Qty To Send" sortKey="qty" align="right" sortConfig={sortConfig} onSort={onSort} />
                <SortTh label="Destination Branch" sortKey="branch" sortConfig={sortConfig} onSort={onSort} />
                <th className="px-3 py-3 text-center font-semibold"><CheckCircle2 className="w-3.5 h-3.5 mx-auto" /></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {group.items.map((item, i) => (
                <DonorItemRow
                  key={`${item.product_code}-${item.destination_branch_code}-${i}`}
                  item={item}
                  onViewTransfer={onViewTransfer}
                  onToggleActioned={onToggleActioned}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Report body (flat printable summary for ONE branch) ──────────────────
function ReportBranchBody({ report, loading }) {
  if (loading) {
    return (
      <div className="py-12 text-center text-gray-400">
        <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" /> Loading report…
      </div>
    );
  }
  if (!report?.has_comparison_data) {
    return (
      <div className="py-12 text-center text-gray-400">
        <Clock className="w-8 h-8 mx-auto mb-2" />
        Not enough upload history yet for this branch — needs at least 2 multi-branch uploads.
      </div>
    );
  }
  return (
    <>
      <p className="text-xs text-gray-500 mb-4">
        Comparing {fmtDate(report.old_upload_date)} → {fmtDate(report.new_upload_date)}
      </p>
      <section>
        <h4 className="font-semibold text-sm text-gray-700 mb-2 flex items-center gap-2">
          <Package className="w-4 h-4" style={{ color: enconOrange }} />
          Covered By Your Own Stock ({report.own_branch_total})
        </h4>
        {report.own_branch_coverage.length === 0 ? (
          <p className="text-xs text-gray-400 mb-4">Nothing to report.</p>
        ) : (
          report.own_branch_coverage.map(g => (
            <div key={g.supplier_name} className="mb-2">
              <p className="text-xs font-semibold text-gray-600 mb-1">{g.supplier_name} ({g.item_count})</p>
              <ul className="text-xs text-gray-600 pl-3 space-y-0.5">
                {g.items.map(it => (
                  <li key={it.product_code}>{it.product_code} — {it.description} (+{fmt(it.net_new_order_qty)})</li>
                ))}
              </ul>
            </div>
          ))
        )}
      </section>
      <section className="mt-5">
        <h4 className="font-semibold text-sm text-gray-700 mb-2 flex items-center gap-2">
          <Truck className="w-4 h-4" style={{ color: enconOrange }} />
          Get From Another Branch ({report.group_total})
        </h4>
        {report.group_transfer_opportunities.length === 0 ? (
          <p className="text-xs text-gray-400 mb-4">Nothing to report.</p>
        ) : (
          report.group_transfer_opportunities.map(g => (
            <div key={g.supplier_name} className="mb-2">
              <p className="text-xs font-semibold text-gray-600 mb-1">{g.supplier_name} ({g.item_count})</p>
              <ul className="text-xs text-gray-600 pl-3 space-y-0.5">
                {g.items.map(it => (
                  <li key={it.product_code}>
                    {it.product_code} — {it.description} (+{fmt(it.net_new_order_qty)}) —{' '}
                    {it.donor_branches?.length > 0
                      ? it.donor_branches.map(d => `get ${fmt(d.qty)} from ${d.branch_code}`).join(', ')
                      : 'no donor found'}
                  </li>
                ))}
              </ul>
            </div>
          ))
        )}
      </section>
      <section className="mt-5">
        <h4 className="font-semibold text-sm text-gray-700 mb-2 flex items-center gap-2">
          <Send className="w-4 h-4" style={{ color: enconOrange }} />
          Send To Another Branch ({report.outbound_total ?? 0})
        </h4>
        {(!report.outbound_transfers || report.outbound_transfers.length === 0) ? (
          <p className="text-xs text-gray-400">Nothing to report.</p>
        ) : (
          report.outbound_transfers.map(g => (
            <div key={g.supplier_name} className="mb-2">
              <p className="text-xs font-semibold text-gray-600 mb-1">{g.supplier_name} ({g.item_count})</p>
              <ul className="text-xs text-gray-600 pl-3 space-y-0.5">
                {g.items.map((it, i) => (
                  <li key={`${it.product_code}-${it.destination_branch_code}-${i}`}>
                    {it.product_code} — {it.description} — send {fmt(it.qty_to_send)} to {it.destination_branch_name} ({it.destination_branch_code})
                  </li>
                ))}
              </ul>
            </div>
          ))
        )}
      </section>
    </>
  );
}

// ─── Report Modal — tabbed per branch ──────────────────────────────────────
// Each branch gets its own tab showing ITS OWN report (what that branch can get
// locally, and where it can get / send stock to / from within the group). This
// is the same data shape the future daily per-branch email will use — one tab
// per branch here maps directly to one email per branch later.
function ReportModal({ branchId, onClose }) {
  const [branches, setBranches] = useState([]);
  const [loadingBranches, setLoadingBranches] = useState(true);
  const [activeBranch, setActiveBranch] = useState(branchId);
  const [reports, setReports] = useState({}); // branchCode -> report data
  const [loadingCode, setLoadingCode] = useState(null); // branchCode currently fetching
  const loadedRef = useRef(new Set());

  // Load the branch list (Trading Points) once, on open
  useEffect(() => {
    let cancelled = false;
    api.get(`${API}/branch-directory`)
      .then(res => {
        if (cancelled) return;
        const list = (res.data || [])
          .filter(b => b.branchCode)
          .sort((a, b) => (a.branchCode || '').localeCompare(b.branchCode || ''));
        setBranches(list);
      })
      .catch(() => toast.error('Failed to load branch list'))
      .finally(() => { if (!cancelled) setLoadingBranches(false); });
    return () => { cancelled = true; };
  }, []);

  const loadReport = useCallback(async (code) => {
    if (loadedRef.current.has(code)) return;
    loadedRef.current.add(code);
    setLoadingCode(code);
    try {
      const res = await api.get(`${API}/stock-ordering/efficiency/report`, { params: { branch_id: code } });
      setReports(prev => ({ ...prev, [code]: res.data }));
    } catch {
      loadedRef.current.delete(code);
      toast.error(`Failed to load report for ${code}`);
    } finally {
      setLoadingCode(prev => prev === code ? null : prev);
    }
  }, []);

  useEffect(() => { if (activeBranch) loadReport(activeBranch); }, [activeBranch, loadReport]);

  const activeReport = reports[activeBranch];
  const activeBranchMeta = branches.find(b => b.branchCode === activeBranch);

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ background: '#fff3e0' }}>
          <div>
            <h3 className="font-bold text-gray-800">
              Stock Efficiency Report{activeBranchMeta ? ` — ${activeBranchMeta.nickname || activeBranchMeta.name}` : ''}
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              One tab per branch — this is what will be emailed out daily once contact addresses are set up.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => window.print()}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold hover:shadow-sm transition-all"
              style={{ borderColor: enconOrange, color: enconOrange }}
            >
              <Printer className="w-3.5 h-3.5" /> Print
            </button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 rounded-full p-1 hover:bg-gray-100">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Branch tabs */}
        <div className="flex items-center gap-1.5 overflow-x-auto px-5 pt-3 pb-3 border-b bg-gray-50">
          {loadingBranches ? (
            <span className="text-xs text-gray-400 flex items-center gap-1.5"><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Loading branches…</span>
          ) : (
            branches.map(b => (
              <button
                key={b.branchCode}
                onClick={() => setActiveBranch(b.branchCode)}
                className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold border-2 transition-colors whitespace-nowrap ${
                  activeBranch === b.branchCode
                    ? 'text-white border-transparent'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-orange-300'
                }`}
                style={activeBranch === b.branchCode ? { backgroundColor: enconOrange } : {}}
                data-testid={`report-branch-tab-${b.branchCode}`}
              >
                {b.nickname || b.name || b.branchCode}
                <span className="ml-1 opacity-70">({b.branchCode})</span>
              </button>
            ))
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          <ReportBranchBody report={activeReport} loading={loadingCode === activeBranch && !activeReport} />
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────
export default function StockEfficiencyView({ user, showReport, onReportShown, oldDate }) {
  const { getEffectiveBranchId } = useBranch();
  const branchId = getEffectiveBranchId ? getEffectiveBranchId() : 'C30';
  const navigate = useNavigate();

  // Open a new Stock Transfer request FROM the clicked donor branch, with this
  // product already added — same sessionStorage handoff GlobalSearch uses.
  const handleCreateTransfer = (item, donor) => {
    sessionStorage.setItem('stock_transfer_prefill', JSON.stringify({
      branchCode: donor.branch_code,
      branchName: donor.branch_name,
      product_code: item.product_code,
      description: item.description,
      stock_unit: item.stock_unit,
      weight: item.weight || 0,
      timestamp: Date.now()
    }));
    navigate('/stock-transfer');
  };

  // Open an EXISTING transfer's read-only detail modal on the Transfer page.
  const handleViewTransfer = (transferId) => {
    sessionStorage.setItem('stock_transfer_view_id', transferId);
    navigate('/stock-transfer');
  };

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedGroup, setExpandedGroup] = useState(new Set());
  const [expandedOwn, setExpandedOwn] = useState(new Set());
  const [reportOpen, setReportOpen] = useState(false);

  // Filter: 'receive' (default — what this branch can get) or 'donor' (what
  // this branch can send elsewhere). Donor data is expensive (checks every
  // other branch), so it's only fetched lazily the first time it's selected.
  const [viewMode, setViewMode] = useState('receive');
  const [donorGroups, setDonorGroups] = useState(null);
  const [donorLoading, setDonorLoading] = useState(false);
  const [expandedDonor, setExpandedDonor] = useState(new Set());
  const donorLoadedRef = useRef(false);

  // Search + branch filter + column sort — shared toolbar, applied per active table
  const [searchQuery, setSearchQuery] = useState('');
  const [branchFilter, setBranchFilter] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, dir: 'asc' });
  const handleSort = (key) => {
    setSortConfig(prev => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`${API}/stock-ordering/efficiency/analysis`, { params: { branch_id: branchId, old_date: oldDate || undefined } });
      setData(res.data);
      // Auto-expand the first supplier group on first load
      setExpandedGroup(prev => prev.size ? prev : new Set((res.data.group_transfer_opportunities || []).slice(0, 1).map(g => g.supplier_name)));
    } catch {
      toast.error('Failed to load Stock Efficiency data');
    } finally {
      setLoading(false);
    }
  }, [branchId, oldDate]);

  useEffect(() => { load(); }, [load]);

  const loadDonor = useCallback(async () => {
    if (donorLoadedRef.current) return;
    donorLoadedRef.current = true;
    setDonorLoading(true);
    try {
      const res = await api.get(`${API}/stock-ordering/efficiency/report`, { params: { branch_id: branchId, old_date: oldDate || undefined } });
      const groups = res.data.outbound_transfers || [];
      setDonorGroups(groups);
      setExpandedDonor(new Set(groups.slice(0, 1).map(g => g.supplier_name)));
    } catch {
      donorLoadedRef.current = false;
      toast.error('Failed to load donor opportunities');
    } finally {
      setDonorLoading(false);
    }
  }, [branchId, oldDate]);

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    setBranchFilter('');
    setSortConfig({ key: null, dir: 'asc' });
    if (mode === 'donor') loadDonor();
  };

  // Re-fetch donor (Transfer Out) data whenever the "change date" pill changes,
  // same as Receiving — so retracing a few days back works on both tabs.
  useEffect(() => {
    donorLoadedRef.current = false;
    setDonorGroups(null);
    if (viewMode === 'donor') loadDonor();
  }, [oldDate]);

  // Distinct branches present in the current view's data, for the branch filter dropdown
  const branchOptions = useMemo(() => {
    const map = new Map();
    if (viewMode === 'donor') {
      (donorGroups || []).forEach(g => g.items.forEach(it => {
        if (it.destination_branch_code) map.set(it.destination_branch_code, it.destination_branch_name);
      }));
    } else {
      (data?.group_transfer_opportunities || []).forEach(g => g.items.forEach(it => {
        (it.donor_branches || []).forEach(d => map.set(d.branch_code, d.branch_name));
      }));
    }
    return Array.from(map.entries()).map(([code, name]) => ({ code, name })).sort((a, b) => a.code.localeCompare(b.code));
  }, [viewMode, donorGroups, data]);

  const filteredGroupGroups = useMemo(
    () => applyFilterSort(data?.group_transfer_opportunities || [], { searchQuery, branchFilter, sortConfig, section: 'group' }),
    [data, searchQuery, branchFilter, sortConfig]
  );
  const filteredDonorGroups = useMemo(
    () => applyFilterSort(donorGroups || [], { searchQuery, branchFilter, sortConfig, section: 'donor' }),
    [donorGroups, searchQuery, branchFilter, sortConfig]
  );

  // Open the report modal when the parent sub-header button is clicked
  useEffect(() => {
    if (showReport) {
      setReportOpen(true);
      onReportShown?.();
    }
  }, [showReport, onReportShown]);

  const toggleSection = (section, supplierName) => {
    const setFn = section === 'own' ? setExpandedOwn : setExpandedGroup;
    setFn(prev => {
      const next = new Set(prev);
      if (next.has(supplierName)) next.delete(supplierName); else next.add(supplierName);
      return next;
    });
  };

  const updateItemLocally = (section, productCode, patch) => {
    setData(prev => {
      if (!prev) return prev;
      const key = section === 'own' ? 'own_branch_coverage' : 'group_transfer_opportunities';
      return {
        ...prev,
        [key]: prev[key].map(g => ({
          ...g,
          items: g.items.map(it => it.product_code === productCode ? { ...it, ...patch } : it),
        })),
      };
    });
  };

  const handleToggleActioned = async (item, section) => {
    const newVal = !item.is_actioned;
    updateItemLocally(section, item.product_code, { is_actioned: newVal });
    try {
      await api.put(`${API}/stock-ordering/efficiency/items/${item.product_code}`, {
        section, is_actioned: newVal,
      }, { params: { branch_id: branchId } });
    } catch {
      updateItemLocally(section, item.product_code, { is_actioned: !newVal });
      toast.error('Failed to update');
    }
  };

  const handleSaveNotes = async (item, section, notes) => {
    updateItemLocally(section, item.product_code, { notes, notes_date: notes.trim() ? new Date().toISOString() : null });
    try {
      await api.put(`${API}/stock-ordering/efficiency/items/${item.product_code}`, {
        section, notes,
      }, { params: { branch_id: branchId } });
    } catch {
      toast.error('Failed to save notes');
    }
  };

  const handleToggleDonorActioned = async (item) => {
    const newVal = !item.is_actioned;
    const patchDonor = (val) => setDonorGroups(prev => (prev || []).map(g => ({
      ...g,
      items: g.items.map(it =>
        it.product_code === item.product_code && it.destination_branch_code === item.destination_branch_code
          ? { ...it, is_actioned: val }
          : it
      ),
    })));
    patchDonor(newVal);
    try {
      await api.put(`${API}/stock-ordering/efficiency/items/${item.product_code}`, {
        section: 'donor', is_actioned: newVal, destination_branch_code: item.destination_branch_code,
      }, { params: { branch_id: branchId } });
    } catch {
      patchDonor(!newVal);
      toast.error('Failed to update');
    }
  };

  if (loading) {
    return (
      <div className="py-20 text-center text-gray-400">
        <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-3 text-orange-300" />
        Loading Stock Efficiency…
      </div>
    );
  }

  const filterPill = (
    <div className="flex items-center gap-2">
      <button
        onClick={() => handleViewModeChange('receive')}
        className={`px-3 py-1.5 rounded-full text-xs font-semibold border-2 transition-colors ${
          viewMode === 'receive' ? 'text-white border-transparent' : 'bg-white text-gray-500 border-gray-200 hover:border-orange-300'
        }`}
        style={viewMode === 'receive' ? { backgroundColor: enconOrange } : {}}
        data-testid="efficiency-filter-receive"
      >
        Receiving
      </button>
      <button
        onClick={() => handleViewModeChange('donor')}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border-2 transition-colors ${
          viewMode === 'donor' ? 'text-white border-transparent bg-blue-600' : 'bg-white text-gray-500 border-gray-200 hover:border-blue-300'
        }`}
        data-testid="efficiency-filter-donor"
      >
        <Send className="w-3 h-3" /> Transfer Out
      </button>
    </div>
  );

  const searchAndBranchBar = (
    <div className="flex items-center gap-2 flex-1 min-w-0 justify-end">
      <div className="relative w-full max-w-[260px]">
        <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
        <input
          type="text"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder="Search code, description, supplier…"
          className="w-full text-xs border border-gray-300 rounded-full pl-8 pr-3 py-1.5 bg-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-400 transition-colors"
          data-testid="efficiency-search-input"
        />
      </div>
      <select
        value={branchFilter}
        onChange={e => setBranchFilter(e.target.value)}
        className="text-xs border border-gray-300 rounded-full px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-orange-300 max-w-[190px]"
        data-testid="efficiency-branch-filter"
      >
        <option value="">All Branches</option>
        {branchOptions.map(b => (
          <option key={b.code} value={b.code}>{b.name} ({b.code})</option>
        ))}
      </select>
    </div>
  );

  if (viewMode === 'donor') {
    return (
      <TooltipProvider delayDuration={150}>
      <div className="space-y-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          {filterPill}
          {searchAndBranchBar}
          <button
            onClick={() => { donorLoadedRef.current = false; loadDonor(); }}
            className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>
        <div>
          <h3 className="flex items-center gap-2 font-bold text-gray-800 mb-3">
            <Send className="w-5 h-5 text-blue-600" />
            Stock Transfer
            {donorGroups && (
              <span className="text-xs px-2 py-0.5 rounded-full font-semibold text-white bg-blue-600">
                {filteredDonorGroups.reduce((sum, g) => sum + g.item_count, 0)}
              </span>
            )}
          </h3>
          {donorLoading ? (
            <div className="py-16 text-center text-gray-400">
              <RefreshCw className="w-7 h-7 animate-spin mx-auto mb-3 text-blue-300" />
              Checking every other branch's stock needs…
            </div>
          ) : filteredDonorGroups.length === 0 ? (
            <p className="text-sm text-gray-400 px-1">
              {donorGroups && donorGroups.length > 0 ? 'No matches for this search/filter.' : 'Nothing today — no other branch currently needs stock this branch can supply.'}
            </p>
          ) : (
            <div className="space-y-2">
              {filteredDonorGroups.map(g => (
                <DonorSupplierGroup
                  key={g.supplier_name}
                  group={g}
                  expanded={expandedDonor.has(g.supplier_name)}
                  onToggle={() => setExpandedDonor(prev => {
                    const next = new Set(prev);
                    if (next.has(g.supplier_name)) next.delete(g.supplier_name); else next.add(g.supplier_name);
                    return next;
                  })}
                  sortConfig={sortConfig}
                  onSort={handleSort}
                  onViewTransfer={handleViewTransfer}
                  onToggleActioned={handleToggleDonorActioned}
                />
              ))}
            </div>
          )}
        </div>
      </div>
      </TooltipProvider>
    );
  }

  if (!data?.has_comparison_data) {
    return (
      <div className="space-y-5">
        <div>{filterPill}</div>
        <div className="flex flex-col items-center justify-center py-20 gap-3 text-center px-6">
          <Clock className="w-12 h-12 text-orange-300" />
          <h3 className="text-lg font-semibold text-gray-700">
            {oldDate ? 'No report found on or before that date' : 'Waiting on a second upload'}
          </h3>
          <p className="text-sm text-gray-500 max-w-md">
            {oldDate ? (
              <>There's no Free Stock Report for {branchId} on or before {fmtDate(oldDate)}. Try an earlier date, or reset to the latest comparison.</>
            ) : (
              <>
                This page compares today's multi-branch Free Stock Report against the previous one to flag
                new demand that's already covered — locally or elsewhere in the group. It needs at least two
                uploads for {branchId} to compare.
                {data?.new_upload_date && (
                  <> So far it has one, from {fmtDate(data.new_upload_date)}.</>
                )}
              </>
            )}
          </p>
          <Button onClick={load} variant="outline" size="sm" className="mt-2">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Check again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={150}>
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        {filterPill}
        {searchAndBranchBar}
        <button
          onClick={load}
          className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>
      <p className="text-xs text-gray-400 -mt-3">
        Comparing {fmtDate(data.old_upload_date)} → {fmtDate(data.new_upload_date)}
      </p>

      {/* Section 2 — Group transfer opportunities */}
      <div>
        <h3 className="flex items-center gap-2 font-bold text-gray-800 mb-3">
          <Truck className="w-5 h-5" style={{ color: enconOrange }} />
          Available From Another Branch
          <span className="text-xs px-2 py-0.5 rounded-full font-semibold text-white" style={{ backgroundColor: enconOrange }}>
            {filteredGroupGroups.reduce((sum, g) => sum + g.item_count, 0)}
          </span>
        </h3>
        {filteredGroupGroups.length === 0 ? (
          <p className="text-sm text-gray-400 px-1">
            {data.group_transfer_opportunities.length > 0 ? 'No matches for this search/filter.' : 'Nothing today — no new orders had group stock available elsewhere.'}
          </p>
        ) : (
          <div className="space-y-2">
            {filteredGroupGroups.map(g => (
              <SupplierGroup
                key={g.supplier_name}
                group={g}
                section="group"
                expanded={expandedGroup.has(g.supplier_name)}
                onToggle={() => toggleSection('group', g.supplier_name)}
                onToggleActioned={(item) => handleToggleActioned(item, 'group')}
                onSaveNotes={(item, notes) => handleSaveNotes(item, 'group', notes)}
                sortConfig={sortConfig}
                onSort={handleSort}
                onCreateTransfer={handleCreateTransfer}
                onViewTransfer={handleViewTransfer}
              />
            ))}
          </div>
        )}
      </div>

      {reportOpen && (
        <ReportModal branchId={branchId} onClose={() => setReportOpen(false)} />
      )}
    </div>
    </TooltipProvider>
  );
}
